// texe-web/src/hooks/useRateLimitMessage.ts
import { useTranslation } from 'react-i18next';

export type RateLimitContext = 
  | 'auth' 
  | 'post' 
  | 'comment' 
  | 'like' 
  | 'savwar' 
  | 'generic';

export interface RateLimitInfo {
  isRateLimited: boolean;
  message: string;
  retryAfter?: number; // seconds
  context: RateLimitContext;
}

/**
 * Rate limit mesajlarını i18n ile yöneten hook
 * 
 * @example
 * ```tsx
 * const { getRateLimitMessage } = useRateLimitMessage();
 * 
 * try {
 *   await apiPost('/api/posts', data);
 * } catch (error) {
 *   if (error.status === 429) {
 *     const info = getRateLimitMessage(error, 'post');
 *     toast.error(info.message);
 *   }
 * }
 * ```
 */
export function useRateLimitMessage() {
  const { t } = useTranslation();

  /**
   * Error objesinden rate limit bilgisini çıkar ve i18n mesajı döndür
   */
  const getRateLimitMessage = (
    error: any,
    context: RateLimitContext = 'generic'
  ): RateLimitInfo => {
    const isRateLimited = 
      error?.status === 429 || 
      error?.message?.includes('rate') ||
      error?.message?.includes('too_many');

    if (!isRateLimited) {
      return {
        isRateLimited: false,
        message: t('error.generic'),
        context,
      };
    }

    // Retry-After header varsa parse et
    const retryAfter = error?.headers?.['retry-after'] 
      ? parseInt(error.headers['retry-after'], 10) 
      : error?.retryAfter 
      ? parseInt(error.retryAfter, 10)
      : undefined;

    // Context'e göre özel mesaj seç
    const messageKey = `rateLimit.${context}`;
    let message = t(messageKey);

    // Fallback to generic if translation missing
    if (message === messageKey) {
      message = t('rateLimit.generic');
    }

    // Retry-after bilgisi varsa ekle
    if (retryAfter && retryAfter > 0 && retryAfter < 300) {
      const retryMessage = t('rateLimit.tryAgainIn', { seconds: retryAfter });
      message = `${message} ${retryMessage}`;
    } else if (retryAfter) {
      message = `${message} ${t('rateLimit.tryAgainSoon')}`;
    }

    return {
      isRateLimited: true,
      message,
      retryAfter,
      context,
    };
  };

  /**
   * Basitleştirilmiş kullanım: sadece mesaj döndür
   */
  const formatRateLimitError = (
    error: any,
    context: RateLimitContext = 'generic'
  ): string => {
    return getRateLimitMessage(error, context).message;
  };

  /**
   * Rate limit olup olmadığını kontrol et
   */
  const isRateLimitError = (error: any): boolean => {
    return (
      error?.status === 429 || 
      error?.message?.includes('rate') ||
      error?.message?.includes('too_many')
    );
  };

  return {
    getRateLimitMessage,
    formatRateLimitError,
    isRateLimitError,
  };
}
